import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { businessAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { useFeedback } from '../../contexts/FeedbackContext';
import { Alert, Spinner } from '../../components/common/UI';
import ProgressTab, { statusClass } from './tabs/ProgressTab';
import FinancialTab from './tabs/FinancialTab';
import WorkedHoursTab from './tabs/WorkedHoursTab';
import InvolvedTab from './tabs/InvolvedTab';
import QrCodeModal from './QrCodeModal';
import {
  ArrowLeftIcon,
  BusinessCardIcon,
  CalendarIcon,
  ClockIcon,
  CreditCardIcon,
  EditIcon,
  TrashIcon,
  UsersIcon,
} from '../../components/common/Icons';
import { getDerivedBusinessStatus } from '../../utils/businessStatus';
import { formatLocalizedDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessClientName } from '../../utils/businessOwner';

const TABS = [
  { key: 'progress', labelKey: 'reports.tabs.progress', icon: CalendarIcon },
  { key: 'financial', labelKey: 'reports.tabs.financial', icon: CreditCardIcon },
  { key: 'hours', labelKey: 'reports.tabs.hours', icon: ClockIcon },
  { key: 'involved', labelKey: 'businessDetails.tabs.involved', icon: UsersIcon },
];

const BusinessDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { confirm, notify } = useFeedback();
  const [activeTab, setActiveTab] = useState('progress');
  const [qrOpen, setQrOpen] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const { data: business, loading, error, refetch } = useFetch(() => businessAPI.getOne(id), [id]);

  const status = useMemo(() => getDerivedBusinessStatus(business?.progressList || []), [business]);

  const handleDelete = async () => {
    const ok = await confirm({
      variant: 'danger',
      title: t('businessDetails.deleteTitle'),
      message: t('businessDetails.deleteMessage'),
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await businessAPI.remove(id);
      navigate('/businesses');
    } catch {
      notify({ variant: 'danger', title: t('businessDetails.deleteErrorTitle'), message: t('businessDetails.deleteError') });
    }
  };

  const handleCopyDescription = async () => {
    if (!business?.description) return;
    try {
      await navigator.clipboard.writeText(business.description);
      notify({ variant: 'success', title: t('businessDetails.descriptionCopied') });
    } catch {
      notify({ variant: 'danger', title: t('businessDetails.copyError') });
    }
  };

  if (loading) return <Spinner size="lg" />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!business) return null;

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <Link
            to="/businesses"
            className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
            aria-label={t('common.back')}
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate text-3xl font-bold tracking-tight text-slate-900">{business.name}</h1>
            <p className="mt-1 text-base text-slate-600">{getBusinessClientName(business, user, t)}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:border-sky-200 hover:text-sky-600"
            onClick={() => setQrOpen(true)}
            type="button"
          >
            <BusinessCardIcon className="h-4 w-4" />
            QR Code
          </button>
          <Link
            to={`/businesses/${id}/edit`}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:border-sky-200 hover:text-sky-600"
          >
            <EditIcon className="h-4 w-4" />
            {t('common.edit')}
          </Link>
          <button
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-red-500 shadow-sm transition hover:border-red-200 hover:bg-red-50"
            onClick={handleDelete}
            type="button"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-4">
        <InfoCard label={t('common.status')}>
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClass(status)}`}>
            {t(`status.${status}`, t('status.pre_initial'))}
          </span>
        </InfoCard>
        <InfoCard label={t('businessDetails.field')} value={business.businessType || t('common.notProvided')} />
        <InfoCard label={t('businessDetails.startDate')} value={formatLocalizedDate(business.startDate, i18n.language) || t('businessDetails.open')} />
        <InfoCard label={t('businessDetails.endDate')} value={formatLocalizedDate(business.endDate, i18n.language) || t('businessDetails.open')} />
      </div>

      {!!business.description && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-bold text-slate-900">{t('businessDetails.descriptionTitle')}</h2>
              <p className="mt-1 text-xs font-semibold text-slate-500">{t('businessDetails.tapDescriptionToCopy')}</p>
            </div>
            <button
              type="button"
              className="inline-flex h-8 items-center rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:border-sky-200 hover:text-sky-600"
              onClick={() => setDescriptionExpanded((current) => !current)}
            >
              {descriptionExpanded ? t('actions.seeLess') : t('actions.seeMore')}
            </button>
          </div>
          <button
            type="button"
            className="block w-full rounded-lg bg-slate-50 p-3 text-left text-sm leading-6 text-slate-800 transition hover:bg-slate-100"
            onClick={handleCopyDescription}
          >
            <span
              style={descriptionExpanded ? { whiteSpace: 'pre-line' } : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                whiteSpace: 'pre-line',
              }}
            >
              {business.description}
            </span>
          </button>
        </section>
      )}

      <div className="mb-6 flex overflow-x-auto rounded-lg bg-slate-200/60 p-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className={`inline-flex h-9 shrink-0 items-center gap-2 rounded-md px-4 text-sm font-semibold transition ${
                isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-700 hover:text-slate-900'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon className="h-4 w-4" />
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {activeTab === 'progress' && <ProgressTab businessId={id} businessName={business.name} data={business.progressList || []} involved={business.involvedPersons || []} onRefresh={refetch} />}
      {activeTab === 'financial' && <FinancialTab businessId={id} data={business.financialEntries || []} involved={business.involvedPersons || []} onRefresh={refetch} />}
      {activeTab === 'hours' && <WorkedHoursTab businessId={id} data={business.workedHours || []} involved={business.involvedPersons || []} onRefresh={refetch} />}
      {activeTab === 'involved' && <InvolvedTab businessId={id} data={business.involvedPersons || []} onRefresh={refetch} />}
      <QrCodeModal isOpen={qrOpen} onClose={() => setQrOpen(false)} businessId={id} />
    </div>
  );
};

const InfoCard = ({ label, value, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
    <p className="text-sm text-slate-600">{label}</p>
    <div className="mt-2 font-bold text-slate-900">{children || value}</div>
  </section>
);

export default BusinessDetailPage;
